import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { useUiLang } from "../../../context/UiLangContext";

interface StopConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function StopConfirmModal({ isOpen, onClose, onConfirm }: StopConfirmModalProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const [isPending, setIsPending] = React.useState(false);

  // Reset pending state when modal is opened/closed
  React.useEffect(() => {
    if (isOpen) {
      setIsPending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    
    setIsPending(true);
    onConfirm();
    
    // Delay closing to prevent touch/click events from bleeding through to underlying elements
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPending) return;
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className={`bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-sm w-full shadow-2xl overflow-hidden animate-[modal-appear_0.18s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)] transition-opacity duration-200 ${
          isPending ? "pointer-events-none opacity-80" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4.5 py-3.5 border-b border-[var(--border)]">
          <div className="flex items-center gap-2 text-[var(--danger)]">
            <AlertTriangle size={16} />
            <h2 className="text-xs font-bold uppercase tracking-wider font-mono">
              {isEn ? "End Session?" : "¿Finalizar Grabación?"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="w-6 h-6 rounded-full bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all disabled:opacity-50"
            aria-label={isEn ? "Close" : "Cerrar"}
          >
            <X size={13} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4.5 py-4.5 flex flex-col gap-4">
          <p className="text-[13px] text-[var(--text)] font-medium leading-relaxed">
            {isEn
              ? "Are you sure you want to stop the recording? The minutes will be generated with the content captured so far."
              : "¿Estás seguro de que querés detener la grabación? La minuta se generará con lo capturado hasta el momento."}
          </p>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--border)]">
            <button
              onClick={handleClose}
              disabled={isPending}
              className="px-3 py-1.5 text-[11.5px] font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] transition-all bg-transparent border border-[var(--border)] rounded-lg cursor-pointer disabled:opacity-50"
            >
              {isEn ? "Keep recording" : "Seguir grabando"}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isPending}
              className="px-3 py-1.5 text-[11.5px] font-bold text-white bg-danger hover:bg-danger/80 active:scale-95 rounded-lg shadow-lg shadow-red-500/10 transition-all cursor-pointer disabled:opacity-50"
            >
              {isEn ? "Yes, stop" : "Sí, detener"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
